import React from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { handleAxiosError } from 'utilities/apiUtils';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Paginator } from 'primereact/paginator';
import Skeleton from 'react-loading-skeleton';
import ViewLinkedPolicy, { type PolicyDetail } from './ViewLinkedPolicy';
import type { PolicyItem } from 'features/dashboard/types/dashboard';
import '../../../styles/panels.css';

const severityByStatus: Record<PolicyItem['status'], 'success' | 'warning' | 'danger' | 'info' | null | undefined> = {
  Active: 'success',
  Expiring: 'warning',
  Cancelled: 'danger',
  Expired: 'danger',
  'Pending Cancel': 'warning',
  'Flat Cancel': 'danger',
  Renewing: 'info',
  Renewed: 'info',
};

export type LinkedPoliciesListProps = {
  title?: string;
  items: PolicyItem[];
  insuredId?: string;
  insuredName?: string;
};

type NowCertsPolicy = {
  databaseId?: string;
  number?: string;
  policyNumber?: string;
  isQuote?: boolean;
  effectiveDate?: string | null;
  expirationDate?: string | null;
  linesOfBusiness?: unknown[];
  status?: string | null;
  [key: string]: unknown;
};

type PagedPoliciesResponse = {
  '@odata.count': number;
  value: NowCertsPolicy[];
};

const toDateLabel = (value?: string | null): string => {
  if (!value) return '';
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = iso ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])) : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const normalizeLob = (lob: string) =>
  lob.replace(/\bWorker's Compensation\b/g, "Workers' Compensation");

const getPolicyTypFromCustomFields = (customFields: unknown): string | null => {
  if (!customFields || typeof customFields !== 'string') return null;
  try {
    const parsed = JSON.parse(customFields);
    if (!Array.isArray(parsed)) return null;
    const entry = parsed.find((item: any) => item?.Key === 'PolicyType');
    return entry?.Value ? String(entry.Value) : null;
  } catch {
    return null;
  }
};

const mapPolicy = (p: NowCertsPolicy): PolicyItem => {
  const customFieldsLob = getPolicyTypFromCustomFields((p as any).customFields);

  const lineOfBusiness = normalizeLob(
    customFieldsLob ??
    (Array.isArray((p as any).lineOfBusinesses) && (p as any).lineOfBusinesses.length > 0
      ? String((p as any).lineOfBusinesses[0]?.lineOfBusinessName || 'Policy')
      : Array.isArray(p.linesOfBusiness) && p.linesOfBusiness.length > 0
        ? String(p.linesOfBusiness[0])
        : 'Policy')
  );

  const effectiveDateLabel = toDateLabel(p.effectiveDate ?? null);
  const expirationDateLabel = toDateLabel(p.expirationDate ?? null);

  const knownStatuses: PolicyItem['status'][] = ['Active', 'Expiring', 'Cancelled', 'Expired', 'Pending Cancel', 'Flat Cancel', 'Renewing', 'Renewed'];
  const rawStatus = p.status ?? '';
  const status: PolicyItem['status'] = knownStatuses.includes(rawStatus as PolicyItem['status'])
    ? (rawStatus as PolicyItem['status'])
    : 'Active';

  return {
    id: String(p.databaseId ?? p.policyNumber ?? `${lineOfBusiness}-${effectiveDateLabel}`),
    policyNumber: p.number || p.policyNumber || String(p.databaseId ?? '') || '-',
    lineOfBusiness,
    effectiveDateLabel,
    expirationDateLabel,
    status,
  };
};

const ROWS = 5;

export default function LinkedPoliciesList({ title = 'Policy List', items, insuredId, insuredName }: LinkedPoliciesListProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [policies, setPolicies] = React.useState<PolicyItem[]>(items);
  const [rawPolicies, setRawPolicies] = React.useState<NowCertsPolicy[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(!!insuredId);
  const [statusFilter, setStatusFilter] = React.useState<'All' | PolicyItem['status']>('All');
  const [selectedPolicy, setSelectedPolicy] = React.useState<PolicyDetail | null>(null);
  const [dialogVisible, setDialogVisible] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [first, setFirst] = React.useState(0);

  // Debounce search input
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Reset to page 1 when filter or search changes
  React.useEffect(() => { setFirst(0); }, [statusFilter, debouncedSearch]);

  React.useEffect(() => {
    if (!insuredId) {
      setPolicies(items);
      setTotalCount(items.length);
      return;
    }

    let cancelled = false;

    const fetchPolicies = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams({ top: String(ROWS), skip: String(first) });
        if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
        if (statusFilter !== 'All') params.set('status', statusFilter);

        let res;
        try {
          res = await axios.post<PagedPoliciesResponse>(
            `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/insured-policies?${params.toString()}`,
            { insuredId },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('igtoken')}`,
                'Content-Type': 'application/json',
                NCToken: localStorage.getItem('NCToken') || ''
              }
            }
          );
        } catch (err) {
          handleAxiosError(err, navigate);
          return;
        }
        if (cancelled || !res) return;

        const raw: NowCertsPolicy[] = Array.isArray(res.data)
          ? (res.data as unknown as NowCertsPolicy[])
          : (res.data?.value ?? []);
        const count = (res.data as any)?.['@odata.count'] ?? raw.length;

        setRawPolicies(raw);
        setTotalCount(count);
        setPolicies(raw.map(mapPolicy));
        setIsLoading(false);
      } catch {
        if (!cancelled) {
          setPolicies([]);
          setRawPolicies([]);
          setTotalCount(0);
          setIsLoading(false);
        }
      }
    };

    fetchPolicies();

    return () => { cancelled = true; };
  }, [insuredId, items, location.key, first, debouncedSearch, statusFilter]);

  const handlePolicyClick = async (policyId: string) => {
    setDialogVisible(true);
    setDetailLoading(true);
    setSelectedPolicy(null);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/policy-details/${encodeURIComponent(policyId)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('igtoken')}`,
            'Content-Type': 'application/json',
            NCToken: localStorage.getItem('NCToken') || '',
          },
        }
      );

      const data = res.data;
      const list = Array.isArray(data) ? data : (data?.value ?? []);
      const detail = list[0] ?? null;

      if (detail) {
        setSelectedPolicy(detail as PolicyDetail);
      } else {
        const mapped = policies.find((p) => p.id === policyId);
        if (mapped) {
          setSelectedPolicy({
            databaseId: mapped.id,
            policyNumber: mapped.policyNumber,
            linesOfBusiness: [mapped.lineOfBusiness],
            status: mapped.status,
          });
        }
      }
    } catch {
      const raw = rawPolicies.find((r) => String(r.databaseId) === policyId || r.policyNumber === policyId);
      if (raw) {
        setSelectedPolicy(raw as PolicyDetail);
      } else {
        const mapped = policies.find((p) => p.id === policyId);
        if (mapped) {
          setSelectedPolicy({
            databaseId: mapped.id,
            policyNumber: mapped.policyNumber,
            linesOfBusiness: [mapped.lineOfBusiness],
            status: mapped.status,
          });
        }
      }
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <section className="panel">
      <header className="panel__header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
        {/* Row 1: Title + count | Filter */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div className="panel__title" style={{ color: '#381574', fontWeight: 600 }}>{title}</div>
            <div className="panel__meta">{totalCount} policies</div>
          </div>
          <select
            className="panel__filterSelect"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'All' | PolicyItem['status'])}
          >
            <option value="All">All</option>
            <option value="Active">Active</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
        {/* Row 2: Search | Add New */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <i className="pi pi-search" style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: '#94a3b8', pointerEvents: 'none' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search policy..."
              className="panel__filterSelect"
              style={{ width: '100%', paddingLeft: 22 }}
            />
          </div>
          {insuredId && (
            <Button
              icon="pi pi-plus"
              label="Add New"
              className="p-button-sm panel__genCertBtn"
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => navigate('/addpolicy', { state: { insuredId, insuredName } })}
            />
          )}
        </div>
      </header>
      <div className="panel__body panel__body--scroll">
        {isLoading ? (
          Array.from({ length: ROWS }).map((_, index) => (
            <div key={index} className="policyRow">
              <div className="policyRow__left">
                <Skeleton width="140px" height="1.2rem" />
                <Skeleton width="100px" height="0.9rem" style={{ marginTop: 4 }} />
                <Skeleton width="120px" height="0.9rem" style={{ marginTop: 4 }} />
              </div>
              <Skeleton width="80px" height="24px" borderRadius="12px" />
            </div>
          ))
        ) : policies.length === 0 ? (
          <div className="panel__emptyState">
            <div className="panel__emptyState__iconWrap">
              <i className="pi pi-shield panel__emptyState__icon" />
            </div>
            <div className="panel__emptyState__title">No policies found</div>
            <div className="panel__emptyState__sub">No policies match the selected filter.</div>
          </div>
        ) : (
          policies.map((p) => (
            <div
              key={p.id}
              className="policyRow policyRow--clickable"
              onClick={() => handlePolicyClick(p.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') handlePolicyClick(p.id); }}
            >
              <div className="policyRow__left">
                <div className="policyRow__number">{p.policyNumber}</div>
                <div className="panel__muted">{p.lineOfBusiness}</div>
                <div className="panel__muted">
                  {p.effectiveDateLabel} - {p.expirationDateLabel}
                </div>
              </div>
              <Tag value={p.status} severity={severityByStatus[p.status]} />
            </div>
          ))
        )}
      </div>

      {totalCount > ROWS && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4, marginBottom: 6 }}>
          <Paginator
            rows={ROWS}
            totalRecords={totalCount}
            first={first}
            onPageChange={(e) => setFirst(e.first)}
            template="PrevPageLink PageLinks NextPageLink"
            className="requests-paginator p-paginator p-component"
          />
        </div>
      )}
      <ViewLinkedPolicy
        visible={dialogVisible}
        onHide={() => { setDialogVisible(false); setSelectedPolicy(null); }}
        policy={selectedPolicy}
        loading={detailLoading}
      />
    </section>
  );
}
